import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Clock, MapPin, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";

export default function BundleDetailPage() {
  const { slug } = useParams();
  const { format } = useCurrency();
  const nav = useNavigate();
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get(`/bundles/${slug}`).then((r) => setB(r.data));
  }, [slug]);

  if (!b) return <div className="pt-32 min-h-screen text-center text-slate-500">Loading...</div>;

  return (
    <div className="pt-32 pb-24 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-sm text-slate-500 mb-4">
          <Link to="/bundles" className="hover:text-[#D4AF37]">Bundles</Link> / <span className="text-blue-950 dark:text-white">{b.title}</span>
        </div>

        {/* Hero */}
        <div className="rounded-3xl overflow-hidden relative h-[380px] mb-8 shadow-2xl">
          <img src={b.image} alt={b.title} className="absolute inset-0 w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"/>
          <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-12">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold bg-[#D4AF37] text-blue-950">{b.tag}</span>
              {b.savings > 0 && (
                <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold bg-emerald-500 text-white flex items-center gap-1">
                  <Sparkles className="w-3 h-3"/> Save {format(b.savings)}
                </span>
              )}
            </div>
            <h1 data-testid="bundle-title" className="text-white text-4xl sm:text-5xl font-bold">{b.title}</h1>
            <p className="text-white/85 mt-2 text-lg">{b.subtitle}</p>
            <div className="flex items-center gap-4 mt-4 text-white/70 text-sm">
              <span className="flex items-center gap-1"><Clock className="w-4 h-4"/>{b.duration}</span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4"/>{b.packages.map(p => p.location).join(" → ")}</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
              <h2 className="text-2xl font-bold text-blue-950 dark:text-white mb-3">About this bundle</h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">{b.description}</p>
            </div>

            {/* Included packages */}
            <div>
              <h2 className="text-2xl font-bold text-blue-950 dark:text-white mb-5">What's included</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {b.packages.map((p) => (
                  <Link key={p.slug} to={`/packages/${p.slug}`} data-testid={`included-${p.slug}`}
                    className="rounded-3xl overflow-hidden card-lift bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
                    <div className="h-40 overflow-hidden">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover"/>
                    </div>
                    <div className="p-4">
                      <div className="font-bold text-blue-950 dark:text-white">{p.title}</div>
                      <div className="text-xs text-slate-500 mt-1">{p.duration} · Regularly {format(p.price)}</div>
                      <div className="mt-3 text-xs text-[#D4AF37] font-semibold flex items-center gap-1">
                        View details <ArrowRight className="w-3 h-3"/>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Perks */}
            <div className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
              <h3 className="text-xl font-bold text-blue-950 dark:text-white mb-4">Bundle perks</h3>
              <ul className="grid sm:grid-cols-2 gap-3 text-sm">
                {[
                  "One concierge across both legs",
                  "Seamless inter-city transfers",
                  "Priority hotel upgrades",
                  "Combined itinerary PDF",
                  "One booking, one payment",
                  "24×7 WhatsApp on-trip support",
                ].map((x) => (
                  <li key={x} className="flex gap-2 text-slate-600 dark:text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5"/>{x}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Booking Sidebar */}
          <aside className="lg:sticky lg:top-28 h-fit">
            <div className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 shadow-2xl">
              <div className="text-xs uppercase tracking-widest text-slate-500">Bundle price</div>
              <div className="text-4xl font-bold text-blue-950 dark:text-white">{format(b.bundle_price)}</div>
              {b.original_price > b.bundle_price && (
                <>
                  <div className="text-sm text-slate-500 line-through">Was {format(b.original_price)}</div>
                  <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold">
                    <Sparkles className="w-3 h-3"/> You save {format(b.savings)} per person
                  </div>
                </>
              )}
              <div className="mt-2 text-xs text-slate-500">/person · {b.duration}</div>
              <button data-testid="book-bundle" onClick={() => nav(`/book/bundle/${b.slug}`)}
                className="mt-6 w-full py-3.5 rounded-full btn-gold text-sm">Book This Bundle</button>
              <a href={`https://wa.me/919891782652?text=Hi%20Your%20Trips!%20I%27m%20interested%20in%20the%20${encodeURIComponent(b.title)}%20bundle.`}
                target="_blank" rel="noreferrer"
                className="mt-3 block text-center py-3.5 rounded-full btn-outline-gold text-sm">
                Ask on WhatsApp
              </a>
              <div className="mt-6 text-xs text-slate-500">
                ✦ 24×7 concierge · Free date change · Best-price guarantee
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
